"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_STEP_SIZE = exports.WORKFLOW_STEP_FLAVOUR = void 0;
exports.workflowPageOf = workflowPageOf;
exports.workflowBlock = workflowBlock;
exports.readEdgelessStepBoxes = readEdgelessStepBoxes;
exports.readEdgelessStepSizes = readEdgelessStepSizes;
exports.readEdgelessWorkflow = readEdgelessWorkflow;
exports.sortKeys = sortKeys;
exports.sameJson = sameJson;
exports.setEdgelessStepBoxes = setEdgelessStepBoxes;
exports.readEdgelessConnectorLines = readEdgelessConnectorLines;
exports.setEdgelessConnectorLines = setEdgelessConnectorLines;
exports.stackEdgelessLoops = stackEdgelessLoops;
exports.writeEdgelessWorkflow = writeEdgelessWorkflow;
exports.setEdgelessStepConfigValue = setEdgelessStepConfigValue;
exports.setEdgelessStepValue = setEdgelessStepValue;
exports.toEdgelessEdge = toEdgelessEdge;
exports.toEdgelessStep = toEdgelessStep;
/**
 * A workflow as an edgeless document: each step is one `affine:workflow-step`
 * block on the surface, each edge one connector between two step blocks.
 * The block ID is the step ID, the connector ID the edge ID, and the output a
 * branch edge leaves from is the `sourcePort` of its connector.
 *
 * These functions read and write the raw Yjs structure BlockSuite keeps in the
 * document, so the server, which never loads the BlockSuite models, reads the
 * same steps and edges as the editor shows.
 *
 * Reading is deterministic: steps sort by ID, edges by source, output and
 * target, so the same content in any order gives the same definition.
 *
 * Imported as `@streamlain/workflow-field-schemas/workflow-edgeless-doc`, not
 * from the index of the package: the index stays free of Yjs for the runner.
 */
const database_identity_1 = require("@streamlain/database-identity");
const fractional_indexing_1 = require("fractional-indexing");
const Y = __importStar(require("yjs"));
const slot_1 = require("./slot");
const workflow_card_metrics_1 = require("./workflow-card-metrics");
exports.WORKFLOW_STEP_FLAVOUR = 'affine:workflow-step';
const PAGE_FLAVOUR = 'affine:page';
const SURFACE_FLAVOUR = 'affine:surface';
const BLOCKS = 'blocks';
/** Height a new step block starts with; the editor sets it to its card's height. */
/** The box of a step block before its card has been measured. */
exports.DEFAULT_STEP_SIZE = Object.freeze({
    w: workflow_card_metrics_1.WORKFLOW_STEP_WIDTH,
    h: 96,
});
/**
 * The fields BlockSuite writes when it creates a connector (ConnectorElementModel
 * field defaults). A model loaded from the document does not fill in a missing
 * field, so a connector written here carries them all.
 */
/**
 * How an edge is drawn: a thin elbowed line, an edge back to a loop dashed. The
 * grey is the theme token `edgeless/frame/border/default` as it shows on the
 * canvas (#0000002b over #fbfbfa, #ffffff2b over #1c1f1b), but opaque: the
 * line and its arrow head are two strokes, and edges into one step share their
 * last stretch, so a see-through colour paints darker wherever they overlap.
 */
const CONNECTOR_FIELDS = {
    seed: 0,
    mode: 1,
    roughness: 1.4,
    stroke: { light: '#d0d0d0', dark: '#414340' },
    strokeStyle: 'solid',
    strokeWidth: 2,
};
const OPTIONAL_STEP_PROPS = [
    'inputMappings',
    'inputSlots',
    'configSlots',
    'outputSlots',
];
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function toJson(value) {
    return value instanceof Y.AbstractType ? value.toJSON() : value;
}
/** The value as BlockSuite stores a plain prop: objects as maps, arrays as arrays. */
function toYValue(value) {
    if (Array.isArray(value)) {
        const array = new Y.Array();
        array.push(value.map(toYValue));
        return array;
    }
    if (isRecord(value)) {
        const map = new Y.Map();
        for (const [key, nested] of Object.entries(value)) {
            map.set(key, toYValue(nested));
        }
        return map;
    }
    return value;
}
/** Explicit single-document adapter for document-only callers. */
function workflowPageOf(doc) {
    const docs = (0, database_identity_1.pageDocumentStructure)(doc);
    return {
        docs,
        writer: new database_identity_1.PageStructureWriter(docs),
        writeBlock: (_parent, _index, write) => write(database_identity_1.PAGE_DOCUMENT_PART_ID),
        writeElement: (_rect, write) => write(database_identity_1.PAGE_DOCUMENT_PART_ID),
    };
}
function workflowBlock(page, id) {
    const entry = page.writer.entries().get(id);
    return entry ? page.docs.partBlocks(entry.s)?.get(id) : undefined;
}
function workflowCanvas(doc, page) {
    const tree = page.writer.tree();
    const surface = [...page.writer.entries()].find(([id, entry]) => entry.k === SURFACE_FLAVOUR && tree.parentOf.has(id))?.[0];
    return surface ? new database_identity_1.PageCanvas(doc, surface, page.docs, page.index) : null;
}
function requireCanvas(doc, page) {
    const canvas = workflowCanvas(doc, page);
    if (!canvas)
        throw new Error('The workflow document has no valid surface');
    return canvas;
}
/** Read only connectors, without decoding unrelated element parts. */
function forEachConnector(canvas, visit) {
    for (const id of canvas.ids()) {
        if (canvas.writer.entries().get(id)?.k !== 'connector')
            continue;
        const element = canvas.get(id);
        if (element)
            visit(element, id);
    }
}
function parseXY(xywh) {
    if (typeof xywh !== 'string') {
        throw new Error('A workflow step block has no position');
    }
    const [x, y] = JSON.parse(xywh);
    if (typeof x !== 'number' || typeof y !== 'number') {
        throw new Error(`A workflow step block has an invalid position ${xywh}`);
    }
    return { x, y };
}
/** Each config slot shows the value of its configuration key. */
function withConfigSlotValues(slots, config) {
    return slots.map(slot => {
        const name = slot['name'];
        if (typeof name !== 'string' || !(name in config))
            return slot;
        return { ...slot, value: (0, slot_1.configSlotValue)(slot, config[name]) };
    });
}
function readStep(id, block) {
    const config = toJson(block.get('prop:config'));
    const step = {
        id,
        type: String(block.get('prop:stepType') ?? ''),
        name: String(block.get('prop:name') ?? ''),
        config: isRecord(config) ? config : {},
        position: parseXY(block.get('prop:xywh')),
    };
    for (const key of OPTIONAL_STEP_PROPS) {
        const value = toJson(block.get(`prop:${key}`));
        if (Array.isArray(value))
            step[key] = value;
    }
    if (step.configSlots) {
        step.configSlots = withConfigSlotValues(step.configSlots, step.config);
    }
    return step;
}
function compareText(a, b) {
    return a < b ? -1 : a > b ? 1 : 0;
}
function compareEdges(a, b) {
    return (compareText(a.sourceStepId, b.sourceStepId) ||
        compareText(a.sourcePort ?? '', b.sourcePort ?? '') ||
        compareText(a.targetStepId, b.targetStepId) ||
        compareText(a.id, b.id));
}
/** The box every step block has now, by step ID: where it lies and how big it is. */
function readEdgelessStepBoxes(doc, page = workflowPageOf(doc)) {
    const boxes = new Map();
    (0, database_identity_1.pagePlacedBlockIds)(page.writer.tree())
        .filter(id => page.writer.entries().get(id)?.k === exports.WORKFLOW_STEP_FLAVOUR)
        .forEach(id => {
        const block = workflowBlock(page, id);
        if (!(block instanceof Y.Map) ||
            block.get('sys:flavour') !== exports.WORKFLOW_STEP_FLAVOUR) {
            return;
        }
        const [x, y, w, h] = JSON.parse(String(block.get('prop:xywh')));
        boxes.set(id, { x, y, w, h });
    });
    return boxes;
}
/** The size every step block has now, by step ID: its card as last measured. */
function readEdgelessStepSizes(doc, page = workflowPageOf(doc)) {
    return new Map([...readEdgelessStepBoxes(doc, page)].map(([id, { w, h }]) => [
        id,
        { w, h },
    ]));
}
/** The steps and edges the document holds; a connector that does not join two steps is no edge. */
function readEdgelessWorkflow(doc, page = workflowPageOf(doc)) {
    const steps = [];
    (0, database_identity_1.pagePlacedBlockIds)(page.writer.tree())
        .filter(id => page.writer.entries().get(id)?.k === exports.WORKFLOW_STEP_FLAVOUR)
        .forEach(id => {
        const block = workflowBlock(page, id);
        if (block instanceof Y.Map &&
            block.get('sys:flavour') === exports.WORKFLOW_STEP_FLAVOUR) {
            steps.push(readStep(id, block));
        }
    });
    steps.sort((a, b) => compareText(a.id, b.id));
    const stepIds = new Set(steps.map(step => step.id));
    const edges = [];
    const canvas = workflowCanvas(doc, page);
    if (canvas)
        forEachConnector(canvas, (element, id) => {
            if (element.get('type') !== 'connector') {
                return;
            }
            const source = element.get('source');
            const target = element.get('target');
            const sourceStepId = isRecord(source) ? source['id'] : undefined;
            const targetStepId = isRecord(target) ? target['id'] : undefined;
            if (typeof sourceStepId !== 'string' ||
                typeof targetStepId !== 'string' ||
                !stepIds.has(sourceStepId) ||
                !stepIds.has(targetStepId)) {
                return;
            }
            const sourcePort = element.get('sourcePort');
            edges.push({
                id,
                sourceStepId,
                targetStepId,
                ...(typeof sourcePort === 'string' ? { sourcePort } : {}),
            });
        });
    edges.sort(compareEdges);
    return { steps, edges };
}
function stepBlock(step, index) {
    const block = new Y.Map();
    block.set('sys:id', step.id);
    block.set('sys:flavour', exports.WORKFLOW_STEP_FLAVOUR);
    block.set('sys:version', 1);
    block.set('prop:stepType', step.type);
    block.set('prop:name', step.name);
    block.set('prop:config', toYValue(step.config));
    for (const key of OPTIONAL_STEP_PROPS) {
        const value = step[key];
        block.set(`prop:${key}`, value ? toYValue(value) : null);
    }
    const { w, h } = exports.DEFAULT_STEP_SIZE;
    block.set('prop:xywh', JSON.stringify([step.position.x, step.position.y, w, h]));
    block.set('prop:index', index);
    block.set('prop:lockedBySelf', false);
    return block;
}
function connector(edge) {
    const element = new Y.Map();
    element.set('type', 'connector');
    element.set('id', edge.id);
    for (const [key, value] of Object.entries(CONNECTOR_FIELDS)) {
        element.set(key, value);
    }
    element.set('source', { id: edge.sourceStepId });
    element.set('target', { id: edge.targetStepId });
    if (edge.sourcePort !== undefined) {
        element.set('sourcePort', edge.sourcePort);
    }
    return element;
}
/** The value with the keys of every object sorted, lists kept in order. */
function sortKeys(value) {
    if (Array.isArray(value))
        return value.map(sortKeys);
    if (!isRecord(value))
        return value;
    const sorted = {};
    for (const key of Object.keys(value).sort()) {
        sorted[key] = sortKeys(value[key]);
    }
    return sorted;
}
/** Whether two JSON values are equal, whatever the order of their keys. */
function sameJson(left, right) {
    return JSON.stringify(sortKeys(left)) === JSON.stringify(sortKeys(right));
}
function setIfChanged(map, key, value) {
    if (!sameJson(toJson(map.get(key)), value))
        map.set(key, toYValue(value));
}
/** Writes the configuration key by key, so a change to one key leaves the others alone. */
function writeConfig(block, config) {
    const current = block.get('prop:config');
    if (!(current instanceof Y.Map)) {
        block.set('prop:config', toYValue(config));
        return;
    }
    for (const key of current.keys()) {
        if (!(key in config))
            current.delete(key);
    }
    for (const [key, value] of Object.entries(config)) {
        if (!sameJson(toJson(current.get(key)), value)) {
            current.set(key, toYValue(value));
        }
    }
}
function updateStep(block, step) {
    setIfChanged(block, 'prop:stepType', step.type);
    setIfChanged(block, 'prop:name', step.name);
    writeConfig(block, step.config);
    for (const key of OPTIONAL_STEP_PROPS) {
        setIfChanged(block, `prop:${key}`, step[key] ?? null);
    }
}
/**
 * Puts step blocks into the boxes given by step ID; a block whose box is
 * already right stays untouched.
 */
function setEdgelessStepBoxes(doc, boxes, page = workflowPageOf(doc)) {
    doc.transact(() => {
        for (const [id, { x, y, w, h }] of boxes) {
            const block = workflowBlock(page, id);
            if (!(block instanceof Y.Map)) {
                throw new Error(`The workflow document has no step "${id}"`);
            }
            const xywh = JSON.stringify([x, y, w, h]);
            if (block.get('prop:xywh') !== xywh)
                block.set('prop:xywh', xywh);
        }
    });
}
function isAnchor(value) {
    return (Array.isArray(value) &&
        value.length === 2 &&
        value.every(coordinate => typeof coordinate === 'number'));
}
/**
 * The line every connector between two steps has now, by connector ID:
 * where its ends meet the steps. A connector whose ends were never placed
 * on its steps has none.
 */
function readEdgelessConnectorLines(doc, page = workflowPageOf(doc)) {
    const lines = new Map();
    const canvas = workflowCanvas(doc, page);
    if (!canvas)
        return lines;
    const steps = readEdgelessStepBoxes(doc, page);
    forEachConnector(canvas, (element, id) => {
        if (element.get('type') !== 'connector') {
            return;
        }
        const [source, target] = [element.get('source'), element.get('target')];
        if (isRecord(source) &&
            isRecord(target) &&
            steps.has(String(source['id'])) &&
            steps.has(String(target['id'])) &&
            isAnchor(source['position']) &&
            isAnchor(target['position'])) {
            lines.set(id, {
                source: source['position'],
                target: target['position'],
                loopBack: element.get('strokeStyle') === 'dash',
            });
        }
    });
    return lines;
}
/**
 * Draws connectors as edges of the workflow, by connector ID: their ends
 * meet the steps at the given anchors, and they take the look of an edge
 * whoever drew them. What is already right stays untouched.
 */
function setEdgelessConnectorLines(doc, lines, page = workflowPageOf(doc)) {
    const canvas = requireCanvas(doc, page);
    const setField = (element, key, value) => {
        if (!sameJson(element.get(key), value))
            element.set(key, value);
    };
    doc.transact(() => {
        for (const [id, line] of lines) {
            const element = canvas.get(id);
            if (!element) {
                throw new Error(`The workflow document has no connector "${id}"`);
            }
            for (const [key, value] of Object.entries(CONNECTOR_FIELDS)) {
                setField(element, key, value);
            }
            if (line.loopBack)
                setField(element, 'strokeStyle', 'dash');
            for (const key of ['source', 'target']) {
                const end = element.get(key);
                if (!isRecord(end))
                    continue;
                setField(element, key, { id: end['id'], position: [...line[key]] });
            }
        }
    });
}
/**
 * Puts every loop under the steps it repeats, nested loops included, so
 * they stay on top of its frame. `bodies` maps a loop to those steps.
 */
function stackEdgelessLoops(doc, bodies, page = workflowPageOf(doc)) {
    const indexOf = (id) => {
        const block = workflowBlock(page, id);
        const index = block instanceof Y.Map ? block.get('prop:index') : null;
        if (typeof index !== 'string') {
            throw new Error(`The workflow document has no step "${id}"`);
        }
        return index;
    };
    doc.transact(() => {
        // An inner loop goes under its body first, then the outer loop under it.
        const loops = [...bodies].sort(([, a], [, b]) => a.length - b.length);
        for (const [loopId, body] of loops) {
            if (body.length === 0)
                continue;
            const lowest = body.map(indexOf).reduce((a, b) => (a < b ? a : b));
            if (indexOf(loopId) < lowest)
                continue;
            page.writer.setLayer(loopId, (0, fractional_indexing_1.generateKeyBetween)(null, lowest));
        }
    });
}
function updateConnector(canvas, id, element, edge) {
    // Surface elements keep their fields as plain values, not as Yjs maps.
    for (const [key, stepId] of [
        ['source', edge.sourceStepId],
        ['target', edge.targetStepId],
    ]) {
        const end = element.get(key);
        if (!isRecord(end) || end['id'] !== stepId)
            element.set(key, { id: stepId });
    }
    canvas.writer.syncConnectorEnds(id);
    if (edge.sourcePort === undefined) {
        if (element.has('sourcePort'))
            element.delete('sourcePort');
    }
    else if (element.get('sourcePort') !== edge.sourcePort) {
        element.set('sourcePort', edge.sourcePort);
    }
}
/** The highest layer index of the canvas, so new steps and edges land on top. */
function highestIndex(page) {
    return page.writer.canvasLayerBounds().last;
}
function createSkeleton(doc, createId, access) {
    const blocks = doc.getMap(BLOCKS);
    const writer = access.writer;
    const pageId = createId();
    const surfaceId = createId();
    const page = new Y.Map();
    page.set('sys:id', pageId);
    page.set('sys:flavour', PAGE_FLAVOUR);
    page.set('sys:version', 2);
    page.set('prop:title', new Y.Text());
    blocks.set(pageId, page);
    writer.placeBlock({
        id: pageId,
        parentId: null,
        partId: database_identity_1.PAGE_DOCUMENT_PART_ID,
    });
    const surface = new Y.Map();
    surface.set('sys:id', surfaceId);
    surface.set('sys:flavour', SURFACE_FLAVOUR);
    surface.set('sys:version', 5);
    blocks.set(surfaceId, surface);
    writer.placeBlock({
        id: surfaceId,
        parentId: pageId,
        partId: database_identity_1.PAGE_DOCUMENT_PART_ID,
    });
}
/**
 * Brings the document to the graph in one transaction, touching only what
 * differs: a step keeps its block, its box and every unchanged configuration
 * key, an edge keeps its connector. Only a new step takes its position from
 * the graph; `setEdgelessStepBoxes` places steps. A document nothing was written to yet gets its
 * page and surface first. A connector whose end names an element the
 * document no longer has goes: two people removed a step while the other
 * joined it. Other connectors that join no step stay untouched.
 */
function writeEdgelessWorkflow(doc, graph, createId, page = workflowPageOf(doc)) {
    doc.transact(() => {
        if ((0, database_identity_1.pageRootBlockId)(doc) === null) {
            createSkeleton(doc, createId, page);
        }
        const canvas = requireCanvas(doc, page);
        const writer = canvas.writer;
        let index = highestIndex(page);
        const nextIndex = () => (index = (0, fractional_indexing_1.generateKeyBetween)(index, null));
        const wanted = new Map(graph.steps.map(step => [step.id, step]));
        const existing = new Set();
        (0, database_identity_1.pagePlacedBlockIds)(page.writer.tree())
            .filter(id => page.writer.entries().get(id)?.k === exports.WORKFLOW_STEP_FLAVOUR)
            .forEach(id => {
            const block = workflowBlock(page, id);
            if (block instanceof Y.Map &&
                block.get('sys:flavour') === exports.WORKFLOW_STEP_FLAVOUR) {
                existing.add(id);
            }
        });
        const stepIds = new Set([...existing, ...wanted.keys()]);
        const placedBlocks = new Set((0, database_identity_1.pagePlacedBlockIds)(page.writer.tree()));
        // Edges first: a connector to a removed step goes with the step.
        const wantedEdges = new Map(graph.edges.map(edge => [edge.id, edge]));
        forEachConnector(canvas, (element, id) => {
            if (element.get('type') !== 'connector') {
                return;
            }
            const source = element.get('source');
            const target = element.get('target');
            const dangles = [source, target].some(end => isRecord(end) &&
                typeof end['id'] === 'string' &&
                !stepIds.has(end['id']) &&
                !placedBlocks.has(end['id']) &&
                !canvas.has(end['id']));
            if (dangles) {
                canvas.remove(id);
                return;
            }
            const joinsStep = [source, target].some(end => isRecord(end) && stepIds.has(String(end['id'])));
            if (!joinsStep)
                return;
            const edge = wantedEdges.get(id);
            if (edge)
                updateConnector(canvas, id, element, edge);
            else
                canvas.remove(id);
        });
        for (const id of existing) {
            const step = wanted.get(id);
            if (step) {
                updateStep(workflowBlock(page, id), step);
                continue;
            }
            writer.removeBlock(id);
        }
        for (const step of graph.steps) {
            if (existing.has(step.id))
                continue;
            const fields = stepBlock(step, nextIndex());
            page.writeBlock(canvas.surfaceId, undefined, partId => {
                page.docs.partBlocks(partId).set(step.id, fields);
                writer.placeBlock({ id: step.id, parentId: canvas.surfaceId, partId });
            });
        }
        for (const edge of graph.edges) {
            if (!canvas.has(edge.id)) {
                const order = nextIndex();
                page.writeElement([0, 0, 0, 0], partId => canvas.set(edge.id, connector(edge), order, partId));
            }
        }
    });
}
function findStepBlock(page, stepId) {
    const block = workflowBlock(page, stepId);
    return block instanceof Y.Map &&
        block.get('sys:flavour') === exports.WORKFLOW_STEP_FLAVOUR
        ? block
        : null;
}
/**
 * Sets one key of the configuration of a step; `undefined` removes the key.
 * Returns false when the document has no such step.
 */
function setEdgelessStepConfigValue(doc, stepId, key, value, origin, page = workflowPageOf(doc)) {
    const block = findStepBlock(page, stepId);
    if (!block)
        return false;
    doc.transact(() => {
        const config = block.get('prop:config');
        if (!(config instanceof Y.Map)) {
            block.set('prop:config', toYValue(value === undefined ? {} : { [key]: value }));
        }
        else if (value === undefined) {
            config.delete(key);
        }
        else if (!sameJson(toJson(config.get(key)), value)) {
            config.set(key, toYValue(value));
        }
    }, origin);
    return true;
}
/** The keys of a step `setEdgelessStepValue` writes, with the prop each lives in. */
const STEP_VALUE_PROPS = {
    name: 'prop:name',
    type: 'prop:stepType',
    inputMappings: 'prop:inputMappings',
    inputSlots: 'prop:inputSlots',
    configSlots: 'prop:configSlots',
    outputSlots: 'prop:outputSlots',
    inputSchema: 'prop:inputSchema',
    outputSchema: 'prop:outputSchema',
};
/**
 * Sets one key of a step other than its ID, configuration and position: its
 * name, type, slots, mappings or schema; `undefined` clears a prop.
 * Returns false when the document has no such step.
 */
function setEdgelessStepValue(doc, stepId, key, value, origin, page = workflowPageOf(doc)) {
    const prop = STEP_VALUE_PROPS[key];
    if (!prop) {
        throw new Error(`The key "${key}" of a workflow step is not set this way`);
    }
    const block = findStepBlock(page, stepId);
    if (!block)
        return false;
    doc.transact(() => setIfChanged(block, prop, value ?? null), origin);
    return true;
}
/** An edge of any workflow edit as the document keeps it: ends and output only. */
function toEdgelessEdge(edge) {
    const id = edge['id'];
    const sourceStepId = edge['sourceStepId'] ?? edge['source'];
    const targetStepId = edge['targetStepId'] ?? edge['target'];
    if (typeof id !== 'string' ||
        typeof sourceStepId !== 'string' ||
        typeof targetStepId !== 'string') {
        throw new Error('A workflow edge needs an ID, a source and a target');
    }
    const sourcePort = edge['sourcePort'];
    return {
        id,
        sourceStepId,
        targetStepId,
        ...(typeof sourcePort === 'string' ? { sourcePort } : {}),
    };
}
/** A step of any workflow edit as the document keeps it. */
function toEdgelessStep(step) {
    const { id, type, name, config, position } = step;
    if (typeof id !== 'string' || typeof type !== 'string') {
        throw new Error('A workflow step needs an ID and a type');
    }
    if (!isRecord(position) ||
        typeof position['x'] !== 'number' ||
        typeof position['y'] !== 'number') {
        throw new Error(`The workflow step "${id}" has no position`);
    }
    if (config !== undefined && !isRecord(config)) {
        throw new Error(`The configuration of workflow step "${id}" is not an object`);
    }
    const result = {
        id,
        type,
        name: typeof name === 'string' ? name : '',
        config: config ?? {},
        position: { x: position['x'], y: position['y'] },
    };
    for (const key of OPTIONAL_STEP_PROPS) {
        const value = step[key];
        if (Array.isArray(value))
            result[key] = value;
    }
    return result;
}
